import normalizeCif from 'utils/normalizeCif';
import checkIsValidEmail from 'utils/checkIsValidEmail';

import {
  ENCUMBRANCE_EXCEL_HEADERS,
  ENCUMBRANCE_EXCEL_ROLES,
  ENCUMBRANCE_EXCEL_TYPES,
} from '../constants/encumbranceExcelColumns';
import {
  EncumbranceExcelRow,
  EncumbrancesExcelRawRow,
  EncumbrancesExcelValidationError,
} from '../types/EncumbrancesExcel';
import { parseExcelDate } from './parseExcelDate';
import { parseExcelInteger } from './parseExcelInteger';
import { parseExcelPercentage } from './parseExcelPercentage';

type MapRawRowsResult = {
  rows: EncumbranceExcelRow[];
  errors: EncumbrancesExcelValidationError[];
};

const hasRequiredHeaders = (rawRows: EncumbrancesExcelRawRow[]): boolean => {
  if (!rawRows.length) return false;

  const keys = new Set(Object.keys(rawRows[0] || {}));
  return ENCUMBRANCE_EXCEL_HEADERS.every((header) => keys.has(header));
};

export const mapRawRowsToEncumbranceExcelRows = (
  rawRows: EncumbrancesExcelRawRow[]
): MapRawRowsResult => {
  const rows: EncumbranceExcelRow[] = [];
  const errors: EncumbrancesExcelValidationError[] = [];

  if (!rawRows.length) {
    errors.push({ code: 'NO_ROWS' });
    return { rows, errors };
  }

  if (!hasRequiredHeaders(rawRows)) {
    errors.push({ code: 'MISSING_HEADERS' });
    return { rows, errors };
  }

  rawRows.forEach((rawRow, index) => {
    const rowIndex = index + 2;
    const encumbranceId = String(rawRow.ENCUMBRANCE_ID || '').trim();
    const type = String(rawRow.TYPE || '').trim().toUpperCase();
    const role = String(rawRow.ROLE || '').trim().toUpperCase();
    const holderCif = normalizeCif(String(rawRow.HOLDER_CIF || ''));
    const holderName = String(rawRow.HOLDER_NAME || '').trim();
    const partyCif = normalizeCif(String(rawRow.PARTY_CIF || ''));
    const partyName = String(rawRow.PARTY_NAME || '').trim();
    const partyEmail = String(rawRow.PARTY_EMAIL || '').trim();
    const comments = String(rawRow.COMMENTS || '').trim();
    const shareFrom = parseExcelInteger(rawRow.SHARE_FROM);
    const shareTo = parseExcelInteger(rawRow.SHARE_TO);
    const totalShares = parseExcelInteger(rawRow.TOTAL_SHARES);
    const date = parseExcelDate(rawRow.DATE);
    const endDate = parseExcelDate(rawRow.END_DATE);
    const percentage = parseExcelPercentage(rawRow.PERCENTAGE);

    if (!encumbranceId) {
      errors.push({ code: 'MISSING_ENCUMBRANCE_ID', rowIndex });
      return;
    }

    if (!type || !ENCUMBRANCE_EXCEL_TYPES.has(type)) {
      errors.push({ code: 'INVALID_TYPE', encumbranceId, rowIndex, detail: type });
    }

    if (!role || !ENCUMBRANCE_EXCEL_ROLES.has(role)) {
      errors.push({ code: 'INVALID_ROLE', encumbranceId, rowIndex, detail: role });
    }

    if (!holderCif) {
      errors.push({ code: 'MISSING_HOLDER_CIF', encumbranceId, rowIndex });
    }

    if (!date) {
      errors.push({ code: 'INVALID_DATE', encumbranceId, rowIndex, cif: holderCif });
    }

    if (rawRow.END_DATE && !endDate) {
      errors.push({
        code: 'INVALID_END_DATE',
        encumbranceId,
        rowIndex,
        cif: holderCif,
      });
    }

    if (
      totalShares === null ||
      shareFrom === null ||
      shareTo === null ||
      shareFrom <= 0 ||
      shareTo < shareFrom
    ) {
      errors.push({ code: 'INVALID_RANGE', encumbranceId, rowIndex, cif: holderCif });
    }

    if (
      totalShares !== null &&
      shareFrom !== null &&
      shareTo !== null &&
      totalShares !== shareTo - shareFrom + 1
    ) {
      errors.push({
        code: 'RANGE_SHARES_MISMATCH',
        encumbranceId,
        rowIndex,
        cif: holderCif,
      });
    }

    if (endDate && date && endDate < date) {
      errors.push({
        code: 'INVALID_END_DATE',
        encumbranceId,
        rowIndex,
        cif: holderCif,
      });
    }

    if (role === 'CO_OWNER' || role === 'USUFRUCTUARY') {
      if (!partyCif) {
        errors.push({
          code: 'MISSING_PARTY_CIF',
          encumbranceId,
          rowIndex,
          cif: holderCif,
        });
      }

      if (!partyEmail) {
        errors.push({
          code: 'MISSING_PARTY_EMAIL',
          encumbranceId,
          rowIndex,
          cif: partyCif || holderCif,
        });
      } else if (!checkIsValidEmail(partyEmail)) {
        errors.push({
          code: 'INVALID_PARTY_EMAIL',
          encumbranceId,
          rowIndex,
          cif: partyCif || holderCif,
        });
      }
    }

    if (role === 'CREDITOR') {
      if (!partyName) {
        errors.push({
          code: 'MISSING_PARTY_NAME',
          encumbranceId,
          rowIndex,
          cif: holderCif,
        });
      }

      if (partyEmail && !checkIsValidEmail(partyEmail)) {
        errors.push({
          code: 'INVALID_PARTY_EMAIL',
          encumbranceId,
          rowIndex,
          cif: holderCif,
        });
      }
    }

    if (role === 'CO_OWNER' && percentage === null) {
      errors.push({
        code: 'MISSING_PERCENTAGE',
        encumbranceId,
        rowIndex,
        cif: partyCif || holderCif,
      });
    }

    if (percentage !== null && (percentage < 0 || percentage > 100)) {
      errors.push({
        code: 'INVALID_PERCENTAGE',
        encumbranceId,
        rowIndex,
        cif: partyCif || holderCif,
        detail: String(percentage),
      });
    }

    if (
      !encumbranceId ||
      !type ||
      !role ||
      !holderCif ||
      !date ||
      shareFrom === null ||
      shareTo === null ||
      totalShares === null
    ) {
      return;
    }

    rows.push({
      rowIndex,
      encumbranceId,
      type,
      shareFrom,
      shareTo,
      totalShares,
      date,
      endDate,
      holderCif,
      holderName,
      role,
      partyCif,
      partyName,
      partyEmail,
      percentage,
      comments,
    });
  });

  return { rows, errors };
};
